/**
 * SignLink — the public signing ceremony, at `/sign/link#<token>`.
 *
 * ── ONE ROUTE FOR EVERY SIGNER WITHOUT AN ACCOUNT ─────────────────────────────
 * Two people sign a Primelane document without ever having a login, and both hold a
 * token link instead:
 *
 *   WITNESS  signs the witness agreement — their own instrument, not the hirer's, which
 *            seats the driver and Primelane alone. The text is frozen when the link is
 *            issued and `content_sha256` is the hash taken when it froze.
 *   DRIVER   the PAYER of a deposit, often weeks before a vehicle is assigned, a contract
 *            exists or an account could be made. They sign FIRST, so nothing has frozen
 *            the text: it is rendered LIVE, `content_sha256` covers exactly those bytes,
 *            and the hash submitted with their signature is what seals the document.
 *
 * Both are cross-checked against a digest this browser takes over what it actually
 * displayed, and a disagreement blocks signing rather than being papered over.
 *
 * `party` on the lookup payload decides which ceremony this is; every word that differs
 * between them lives in `sign-link-ceremony.ts`, and so does the rule about which hash may
 * be submitted. `/sign/witness` still resolves here — App.tsx redirects it, fragment and
 * all — because links already shared cannot be recalled.
 *
 * ── NO AUTH, AND THAT IS THE DESIGN ───────────────────────────────────────────
 * A link-signer is not a user of any Primelane app: no account, no password, no invitation
 * email in v1. What they hold instead is a 256-bit token in the URL fragment, which
 * browsers never send to a server, and every call goes to `sign-agreement-public` with the
 * publishable key and NO session. This route therefore sits outside `RequireAuth`, and it
 * must stay outside it: somebody who happened to be signed in as another person would
 * otherwise have that session attached to their signature.
 *
 * ── THE FRAGMENT IS READ ONCE AND THEN ERASED ─────────────────────────────────
 * The network never sees a fragment, but the address bar and every reporter that reads
 * `location.href` do. The token is therefore lifted into memory on the first render and the
 * fragment is stripped from the URL immediately afterwards, so nothing that samples the
 * page later — telemetry, a screenshot, a shoulder — can pick the credential back up. It
 * lives in component state for the rest of the visit and is written nowhere else; a reload
 * loses it, which is why the incomplete-link notice says to reopen the original link.
 *
 * ── WHAT THE PAGE KNOWS ───────────────────────────────────────────────────────
 * Exactly what the endpoint projects: the document text, the signer's own name, the
 * driver's FIRST name, the vehicle registration where there is one, the title, the
 * reference, the role and the expiry. No ids, no storage paths, no phone numbers, no
 * financial values — so there is nothing here to leak even if the link is forwarded.
 *
 * ── ERRORS ────────────────────────────────────────────────────────────────────
 * Every refusal is one constant sentence, because anything else turns this page into a
 * token oracle. Three codes are passed through and shown as themselves; they are raised
 * only after the token has been accepted, and withholding them would leave a signer with
 * a stale tab retrying the same stale hash forever.
 *
 * A REFUSAL IS SOMETHING THE SERVER SAID. A submit that never reached the server said
 * nothing, and this page used to answer it with the refusal anyway — so a signer whose POST
 * died on a cold start was told their link had expired while it sat there, perfectly valid.
 * `signLinkFailure` is now the only reading of a failure on this page, and the constant
 * sentence is reserved for the failures that carry a verdict. A dead connection gets the
 * truth and a way to try again — automatically once for the submit (see
 * `signLinkSignWithRetry`), on a button for the page-load lookup.
 */

import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ArrowRight, Eye, Loader2, RotateCw, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { captureError } from '@/lib/logger';
import { agreementMarkdownToPages } from '@/lib/agreement-markdown';
import {
    CONSENT_TEXT_VERSION,
    SIGNING_DISABLED,
    clearDraft,
    loadDraft,
    saveDraft,
    sha256Hex,
    readerGateOpen,
} from '@/lib/esign';
import {
    SIGN_LINK_GENERIC_MESSAGE,
    SIGN_LINK_NETWORK_MESSAGE,
    SIGN_LINK_NETWORK_SIGN_MESSAGE,
    SignLinkError,
    signLinkFailure,
    signLinkLookup,
    signLinkSignWithRetry,
    signLinkSignedCopy,
    tokenFromHash,
    type SignLinkDocument,
} from '@/lib/signLink';
import {
    hashAttestation,
    normalizeSignLinkParty,
    signLinkCeremony,
} from '@/lib/sign-link-ceremony';
import CeremonyShell, { CeremonyNote } from '@/components/agreements/CeremonyShell';
import {
    ConfirmRow,
    DoneMark,
    Eyebrow,
    SigningAs,
    StepHeading,
} from '@/components/agreements/CeremonyPieces';
import DocumentReader from '@/components/agreements/DocumentReader';
import SignaturePad, { type SignaturePadHandle } from '@/components/agreements/SignaturePad';

/**
 * LAZY, AND THIS ONE IS LOAD-BEARING. `App.tsx` imports this page eagerly — a link-signer's
 * first paint is the whole point of it — so a static import of the sealed reader would put
 * `pdfjs-dist` in the entry chunk of every visit to every route in the app. It shares the
 * chunk with `/agreement/:documentId`, which imports it the same way.
 */
const SealedPdfViewer = lazy(() => import('@/components/agreements/SealedPdfViewer'));

const STEPS = ['identity', 'read', 'attest', 'sign', 'done'] as const;
type Step = (typeof STEPS)[number];

export default function SignLink() {
    // Lazy state, not a memo: the fragment is gone by the second render, so the value has to
    // be captured on the first one and then never recomputed.
    const [token] = useState<string | null>(() => tokenFromHash(window.location.hash));

    const [step, setStep] = useState<Step>('identity');
    const [page, setPage] = useState(1);
    const [pagesSeen, setPagesSeen] = useState(1);
    const [readSeconds, setReadSeconds] = useState(0);
    const [confirmations, setConfirmations] = useState<boolean[]>([]);
    const [typedName, setTypedName] = useState('');
    const [hasInk, setHasInk] = useState(false);
    const [signatureProof, setSignatureProof] = useState<string | null>(null);
    // The digest of the displayed markdown, kept WITH the text it was taken over. `value` is
    // null where `crypto.subtle` is unavailable (an insecure context).
    const [digest, setDigest] = useState<{ source: string; value: string | null } | null>(null);
    const [signed, setSigned] = useState<SignLinkDocument | null>(null);

    const padRef = useRef<SignaturePadHandle | null>(null);
    const draftRestored = useRef(false);

    // The token is in memory now, so the copy in the address bar is pure liability: it is
    // what a breadcrumb, a pageload transaction or a shared screenshot would carry off.
    // Same path, same query, same title — only the fragment goes.
    useEffect(() => {
        if (window.location.hash.length === 0) return;
        try {
            window.history.replaceState(
                window.history.state,
                window.document.title,
                `${window.location.pathname}${window.location.search}`,
            );
        } catch {
            // A sandboxed context that refuses replaceState costs the tidy URL, never the
            // ceremony — the token has already been read.
        }
    }, []);

    const lookup = useQuery({
        queryKey: ['sign-link-token-lookup'],
        queryFn: async () => await signLinkLookup(token as string),
        enabled: !!token && !SIGNING_DISABLED,
        retry: false,
        // The token is a credential; a background refetch is a second authentication
        // attempt against the per-IP ledger for no gain.
        refetchOnWindowFocus: false,
        staleTime: Infinity,
    });

    const doc = signed ?? lookup.data ?? null;
    const party = normalizeSignLinkParty(doc?.party);

    // WHICH STEP IS RENDERED, WHICH IS NOT ALWAYS THE STEP IN STATE. `already_signed` is known the
    // moment the payload lands; the effect below that acts on it is passive and runs AFTER the
    // browser has painted, so a page driven by `step` alone shows the identity step — "You're
    // signing as …", with the button that starts the ceremony — for one frame before flipping to
    // done. To the one person this arm exists for that frame reads as being asked to sign again.
    // Deriving it here costs nothing and cannot disagree with the effect: both answer 'done' from
    // the same two values.
    const activeStep: Step = signed !== null || doc?.already_signed === true ? 'done' : step;

    const copy = useMemo(
        () =>
            signLinkCeremony(party, {
                // `signer_name` is the general key; `witness_name` still answers for a
                // payload issued before the endpoint carried one.
                signerName: doc?.signer_name ?? doc?.witness_name ?? null,
                driverFirstName: doc?.driver_first_name ?? null,
                roleLabel: doc?.role_label ?? null,
                vehicleRegistration: doc?.vehicle_registration ?? null,
                referenceNo: doc?.reference_no ?? null,
                expiresLabel: doc?.expires_at
                    ? format(new Date(doc.expires_at), 'MMM d, yyyy')
                    : null,
                consentVersion: CONSENT_TEXT_VERSION,
            }),
        [party, doc],
    );

    const draftScope = doc?.reference_no ? `${copy.draftPrefix}:${doc.reference_no}` : '';

    const pages = useMemo(() => agreementMarkdownToPages(doc?.markdown), [doc]);

    // Digest the bytes on screen. It cross-checks the hash the endpoint sent for both
    // parties — and stands in as the attestation for a payer whose payload carried none,
    // which is the only case `hashAttestation` allows a self-computed hash.
    useEffect(() => {
        const source = doc?.markdown ?? null;
        if (source === null) return;
        let cancelled = false;
        void sha256Hex(source).then((value) => {
            if (!cancelled) setDigest({ source, value });
        });
        return () => {
            cancelled = true;
        };
    }, [doc]);

    // THE DIGEST COUNTS ONLY FOR THE BYTES IT WAS TAKEN OVER. It is carried with its source
    // rather than reset on the way to a new one, so a re-lookup that serves different text
    // cannot leave the previous document's hash standing as this one's attestation for the
    // frame before the new digest lands.
    const localDigest = doc?.markdown && digest?.source === doc.markdown ? digest.value : null;

    const attestation = hashAttestation(party, doc?.content_sha256, localDigest);

    // A primitive, so the effects below depend on the number of statements rather than on
    // the identity of the object that happens to hold them.
    const statementCount = copy.statements.length;

    // The tick-boxes follow the party's statements; a restored draft of another length is
    // dropped rather than leaving a gate counting boxes that are no longer on screen.
    useEffect(() => {
        setConfirmations((previous) =>
            previous.length === statementCount
                ? previous
                : Array.from({ length: statementCount }, () => false),
        );
    }, [statementCount]);

    // Somebody who already signed lands on the done step rather than being walked through a
    // ceremony whose outcome already exists.
    //
    // THIS BRANCH WAS UNREACHABLE UNTIL THE SERVER COULD SAY SO. `agreement_token_lookup` refused
    // every consumed token with the constant sentence, so a signer who reopened their own link —
    // a refresh, a restored tab, a submit whose response was lost — was shown "this signing link
    // is invalid or has expired" about a link that had just worked and a signature that exists.
    // 20260818020000 answers that one state with `already_signed`, and this is the effect that
    // was already waiting for it.
    useEffect(() => {
        if (lookup.data?.already_signed && !signed) setStep('done');
    }, [lookup.data, signed]);

    // No restore without a byte identity: a draft resumed against different text would be a
    // checklist ticked over a document nobody read.
    useEffect(() => {
        if (draftRestored.current || !draftScope || !doc || !attestation.hash) return;
        // A CEREMONY WHOSE OUTCOME EXISTS HAS NO DRAFT TO RESUME. The effect above puts this page
        // on the done step, and both effects run in the same commit — so a restore here would set
        // the step a second time and win, walking somebody who has already signed back into the
        // reading gate for a document they cannot sign again. The already-signed payload carries
        // no markdown and no hash, so `attestation.hash` is normally null and this never runs at
        // all; the guard is here for the narrow case where the token is consumed between the
        // endpoint's two reads and a full payload comes back flagged.
        if (doc.already_signed) return;
        draftRestored.current = true;
        const draft = loadDraft(draftScope, attestation.hash);
        if (!draft) return;
        const restored = (STEPS as readonly string[]).includes(draft.step)
            ? (draft.step as Step)
            : 'identity';
        setStep(restored === 'done' ? 'sign' : restored);
        setPage(Math.max(1, draft.page ?? 1));
        setPagesSeen(Math.max(1, draft.pagesSeen ?? 1));
        setReadSeconds(Math.max(0, draft.readSeconds ?? 0));
        setTypedName(draft.typedName ?? '');
        if (Array.isArray(draft.terms) && draft.terms.length === statementCount) {
            setConfirmations(draft.terms);
        }
    }, [draftScope, doc, attestation.hash, statementCount]);

    useEffect(() => {
        if (!draftScope || !doc || !attestation.hash || activeStep === 'done') return;
        saveDraft(draftScope, {
            step,
            page,
            pagesSeen,
            readSeconds,
            terms: confirmations,
            typedName,
            contentSha256: attestation.hash,
        });
    }, [
        draftScope,
        doc,
        attestation.hash,
        step,
        // The RENDERED step decides whether there is a draft to keep; the STATE step is what
        // gets written into it. They differ only on the already-signed arm, where nothing is
        // written at all.
        activeStep,
        page,
        pagesSeen,
        readSeconds,
        confirmations,
        typedName,
    ]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [step]);

    const signMutation = useMutation({
        mutationFn: async () => {
            if (!token || !attestation.hash) throw new SignLinkError(SIGN_LINK_GENERIC_MESSAGE);
            const dataUrl = padRef.current?.toDataUrl();
            if (!dataUrl) throw new SignLinkError('Draw your signature before submitting.');

            // One retry lives inside this call, and only for a submit that never reached a
            // verdict; `isPending` therefore covers both attempts and the button stays busy
            // rather than flickering back to idle between them.
            const result = await signLinkSignWithRetry({
                token,
                contentSha256: attestation.hash,
                typedName: typedName.trim(),
                signaturePng: dataUrl,
                consentTextVersion: CONSENT_TEXT_VERSION,
            });
            return { result, dataUrl };
        },
        onSuccess: ({ result, dataUrl }) => {
            setSignatureProof(dataUrl);
            setSigned(result);
            setStep('done');
            clearDraft(draftScope);
            toast.success(copy.done.toast);
        },
        onError: (error) => {
            captureError(error, { tags: { area: 'sign-link' } });
            const info = signLinkFailure(error);
            // The constant sentence is the ENDPOINT's, so it is shown only where the endpoint
            // answered. A submit that never arrived is told so, after the retry above has
            // already failed too — never a reason of our own invention, in either direction.
            toast.error(info.network ? SIGN_LINK_NETWORK_SIGN_MESSAGE : info.message);
            if (info.code === 'AGREEMENT_CONTENT_CHANGED') {
                clearDraft(draftScope);
                setStep('read');
                setPage(1);
                setPagesSeen(1);
                setReadSeconds(0);
                void lookup.refetch();
            }
        },
    });

    /**
     * The executed copy — READ HERE, never saved.
     *
     * ── WHAT CHANGED, AND WHY IT IS STILL A QUERY ON A BUTTON ─────────────────
     * This used to be a mutation that ended in an object URL, an anchor with a `download`
     * attribute and a synthetic click. The owner's ruling (2026-08-21) closes every download
     * in this app, so it now fetches BYTES and the done step paints them.
     *
     * It stays behind a press rather than loading with the step. A link-signer has finished
     * what they came to do; most of them close the page. Fetching a sealed PDF — and the
     * reader chunk that renders it — for everybody would spend a phone's data on a document
     * most of them will not open, and `enabled` is what keeps that honest.
     *
     * THE TOKEN NEVER LEAVES THE FRAGMENT. It is in memory, it goes out in a POST body, and
     * the bytes come back in the response. Nothing navigates, so nothing carries it into an
     * address bar, a referrer or a reporter.
     */
    const [viewing, setViewing] = useState(false);
    const copyQuery = useQuery({
        queryKey: ['sign-link-signed-copy'],
        queryFn: async (): Promise<ArrayBuffer> => {
            if (!token) throw new SignLinkError(SIGN_LINK_GENERIC_MESSAGE);
            const blob = await signLinkSignedCopy(token);
            return await blob.arrayBuffer();
        },
        enabled: viewing && !!token,
        // The sealed instrument is immutable; one fetch per visit is all it can need.
        staleTime: Infinity,
        retry: false,
    });

    useEffect(() => {
        if (!copyQuery.error) return;
        captureError(copyQuery.error, { tags: { area: 'sign-link-signed-copy' } });
    }, [copyQuery.error]);

    const copyFailure = copyQuery.error ? signLinkFailure(copyQuery.error) : null;

    // ── Guard rails ───────────────────────────────────────────────────────────
    if (SIGNING_DISABLED) {
        return (
            <SignLinkNotice
                title="Signing is temporarily unavailable"
                body="Primelane has paused online signing. Please try this link again later."
            />
        );
    }

    if (!token) {
        return (
            <SignLinkNotice
                title="This signing link is incomplete"
                body="Open the full link exactly as it was shared with you — the part after the # is what identifies you. If you refreshed this page, open that original link again."
            />
        );
    }

    if (lookup.isLoading) {
        return (
            <div className="mx-auto w-full max-w-2xl space-y-4 p-6">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-64 w-full" />
                <Skeleton className="h-10 w-full" />
            </div>
        );
    }

    // THE PAGE-LOAD FETCH, SPLIT THE SAME WAY. A lookup that never reached the endpoint has
    // not been refused by it, and a full-page "invalid or has expired" is the wrong verdict to
    // hand somebody whose phone dropped a bar — it reads as final, and this one is not. The
    // retry is theirs to press: a lookup that DID arrive is metered per IP, and pressing is
    // the difference between a signer's own second attempt and a loop nobody asked for.
    if (lookup.error && signLinkFailure(lookup.error).network) {
        return (
            <SignLinkNotice
                title="We could not reach Primelane"
                body="Your link is fine — the connection was not. Check your connection and try again. Nothing has been signed from this page."
                action={{
                    label: 'Try again',
                    busy: lookup.isFetching,
                    onPress: () => void lookup.refetch(),
                }}
            />
        );
    }

    if (lookup.error || !doc) {
        return (
            <SignLinkNotice
                title={SIGN_LINK_GENERIC_MESSAGE}
                body="Ask Primelane for a fresh link. Nothing has been signed from this page."
            />
        );
    }

    const title = doc.title ?? copy.fallbackTitle;

    // ── Steps ─────────────────────────────────────────────────────────────────
    if (activeStep === 'identity') {
        return (
            <CeremonyShell
                title={title}
                stepIndex={0}
                steps={STEPS}
                footer={
                    <>
                        <Button className="w-full" onClick={() => setStep('read')}>
                            {copy.identity.cta}
                            <ArrowRight className="h-4 w-4" />
                        </Button>
                        <CeremonyNote>{copy.identity.note}</CeremonyNote>
                    </>
                }
            >
                <div className="space-y-4">
                    <div>
                        <Eyebrow>{copy.identity.eyebrow}</Eyebrow>
                        <StepHeading>{copy.identity.heading}</StepHeading>
                        <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
                            {copy.identity.lede}
                        </p>
                    </div>

                    <dl className="divide-y divide-border rounded-xl border border-border">
                        {copy.identityRows.map((row) => (
                            <DetailRow key={row.label} label={row.label} value={row.value} />
                        ))}
                    </dl>

                    <p className="text-[11.5px] leading-relaxed text-muted-foreground">
                        {copy.identity.foot}
                    </p>
                </div>
            </CeremonyShell>
        );
    }

    if (activeStep === 'read') {
        const gateOpen = readerGateOpen(
            pagesSeen,
            pages.length,
            readSeconds,
            copy.readSecondsFloor,
        );
        return (
            <CeremonyShell
                title={title}
                stepIndex={1}
                steps={STEPS}
                footer={
                    <>
                        <Button
                            className="w-full"
                            disabled={!gateOpen}
                            onClick={() => setStep('attest')}
                        >
                            Continue
                            <ArrowRight className="h-4 w-4" />
                        </Button>
                        <CeremonyNote>{copy.reader.note}</CeremonyNote>
                    </>
                }
            >
                <DocumentReader
                    pages={pages}
                    page={page}
                    onPageChange={(next) => {
                        setPage(next);
                        setPagesSeen((seen) => Math.max(seen, next));
                    }}
                    pagesSeen={pagesSeen}
                    readSeconds={readSeconds}
                    onReadSecondsChange={setReadSeconds}
                    secondsFloor={copy.readSecondsFloor}
                    frozenNotice={copy.reader.notice}
                />
            </CeremonyShell>
        );
    }

    if (activeStep === 'attest') {
        const allConfirmed =
            confirmations.length === statementCount && confirmations.every(Boolean);
        return (
            <CeremonyShell
                title={title}
                stepIndex={2}
                steps={STEPS}
                footer={
                    <>
                        <Button
                            className="w-full"
                            disabled={!allConfirmed}
                            onClick={() => setStep('sign')}
                        >
                            {copy.confirm.cta}
                            <ArrowRight className="h-4 w-4" />
                        </Button>
                        <CeremonyNote>
                            {allConfirmed ? copy.confirm.allNote : copy.confirm.someNote}
                        </CeremonyNote>
                    </>
                }
            >
                <div className="space-y-4">
                    <div>
                        <Eyebrow>{copy.confirm.eyebrow}</Eyebrow>
                        <StepHeading>{copy.confirm.heading}</StepHeading>
                    </div>
                    <div>
                        {copy.statements.map((statement, index) => (
                            <ConfirmRow
                                key={statement.title}
                                checked={confirmations[index] === true}
                                onCheckedChange={(checked) =>
                                    setConfirmations((previous) => {
                                        const next = [...previous];
                                        next[index] = checked;
                                        return next;
                                    })
                                }
                                title={statement.title}
                                detail={statement.detail}
                            />
                        ))}
                    </div>
                </div>
            </CeremonyShell>
        );
    }

    if (activeStep === 'sign') {
        const nameReady = typedName.trim().length >= 2;
        const canSubmit =
            nameReady && hasInk && attestation.hash !== null && !signMutation.isPending;
        return (
            <CeremonyShell
                title={title}
                stepIndex={3}
                steps={STEPS}
                footer={
                    <>
                        <Button
                            className="w-full"
                            disabled={!canSubmit}
                            onClick={() => signMutation.mutate()}
                        >
                            {signMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                            {copy.sign.submit}
                        </Button>
                        <CeremonyNote>{copy.sign.note}</CeremonyNote>
                    </>
                }
            >
                <div className="space-y-4">
                    <div>
                        <Eyebrow>{copy.sign.eyebrow}</Eyebrow>
                        <StepHeading>{copy.sign.heading}</StepHeading>
                    </div>

                    {attestation.blocked === 'MISMATCH' && (
                        <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-3.5 text-[12.5px] text-destructive">
                            <p className="font-semibold">This document has changed</p>
                            <p className="mt-1">
                                The text on screen no longer matches what Primelane has on record.
                                Reload this page and read it again.
                            </p>
                        </div>
                    )}

                    {attestation.blocked === 'UNVERIFIABLE' && (
                        <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-3.5 text-[12.5px] text-destructive">
                            <p className="font-semibold">We cannot verify this document</p>
                            <p className="mt-1">
                                Your browser could not confirm the exact text of this document, so
                                it cannot be signed here. Open the original link again over a
                                secure (https) connection, or ask Primelane for a fresh link.
                            </p>
                        </div>
                    )}

                    <SigningAs name={copy.signerName} sub={copy.sign.signingAsSub} />

                    <div className="space-y-1.5">
                        <Label htmlFor="sign-link-typed-name" className="text-[12.5px]">
                            {copy.sign.typedNameLabel}
                        </Label>
                        <Input
                            id="sign-link-typed-name"
                            value={typedName}
                            autoComplete="name"
                            placeholder={copy.signerName}
                            onChange={(event) => setTypedName(event.target.value)}
                        />
                    </div>

                    <SignaturePad
                        ref={padRef}
                        onInkChange={setHasInk}
                        caption={format(new Date(), 'MMM d, yyyy')}
                        disabled={signMutation.isPending}
                    />
                </div>
            </CeremonyShell>
        );
    }

    // ── done ──────────────────────────────────────────────────────────────────
    //
    // THE COPY IS OPENED IN PLACE. There is no navigation off this page and no file: the
    // viewer replaces the signature proof once the bytes land, and the link-signer reads what
    // they signed exactly where they signed it.
    return (
        <CeremonyShell
            title={title}
            stepIndex={4}
            steps={STEPS}
            footer={
                viewing ? null : (
                    <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => setViewing(true)}
                    >
                        <Eye className="h-4 w-4" />
                        View your copy
                    </Button>
                )
            }
        >
            <div className="space-y-4">
                <div className={viewing ? 'text-center' : 'pt-8 text-center'}>
                    <DoneMark />
                    <h2 className="mt-4 text-xl font-bold tracking-tight">{copy.done.heading}</h2>
                    <p className="mx-auto mt-2 max-w-sm text-[13px] leading-relaxed text-muted-foreground">
                        {copy.done.body}
                    </p>
                </div>

                {viewing && copyQuery.isLoading && (
                    <div className="flex items-center justify-center gap-2 rounded-xl border border-border py-16 text-[12.5px] text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Opening your copy…
                    </div>
                )}

                {viewing && copyFailure && (
                    <div className="rounded-xl border border-border p-4 text-[12.5px] leading-relaxed text-muted-foreground">
                        <p className="text-[13px] font-bold tracking-tight text-foreground">
                            {copyFailure.network
                                ? 'We could not reach Primelane'
                                : 'Your copy is not ready yet'}
                        </p>
                        <p className="mt-1">
                            {copyFailure.network
                                ? SIGN_LINK_NETWORK_MESSAGE
                                : 'It becomes available once Primelane has countersigned. This link keeps working until it expires.'}
                        </p>
                        {/* Offered only where a second attempt can change the answer — the
                            same rule the page-load lookup's own retry follows. */}
                        {copyFailure.network && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="mt-3"
                                disabled={copyQuery.isFetching}
                                onClick={() => void copyQuery.refetch()}
                            >
                                {copyQuery.isFetching ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <RotateCw className="h-4 w-4" />
                                )}
                                Try again
                            </Button>
                        )}
                    </div>
                )}

                {viewing && copyQuery.data && (
                    <Suspense
                        fallback={
                            <div className="flex items-center justify-center gap-2 rounded-xl border border-border py-16 text-[12.5px] text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Opening your copy…
                            </div>
                        }
                    >
                        <SealedPdfViewer bytes={copyQuery.data} />
                    </Suspense>
                )}

                {!viewing && signatureProof && (
                    <div className="rounded-xl border border-border bg-white p-3">
                        <img
                            src={signatureProof}
                            alt={copy.done.signatureAlt}
                            className="mx-auto h-20 object-contain"
                        />
                        <p className="mt-2 text-center text-[11px] text-muted-foreground">
                            {typedName || copy.signerName} · {doc.reference_no}
                        </p>
                    </div>
                )}
            </div>
        </CeremonyShell>
    );
}

// ─── Small parts ─────────────────────────────────────────────────────────────

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
    return (
        <div className="flex items-baseline justify-between gap-4 px-3.5 py-3">
            <dt className="text-[11.5px] uppercase tracking-wide text-muted-foreground">{label}</dt>
            <dd className="min-w-0 truncate text-[13px] font-medium text-foreground">
                {value || '—'}
            </dd>
        </div>
    );
}

/**
 * `action` is offered only where trying again can actually change the answer — which is the
 * transport failure and nothing else. A notice about a token's state does not get a button
 * that re-asks the same question of the same ledger.
 */
function SignLinkNotice({
    title,
    body,
    action,
}: {
    title: string;
    body: string;
    action?: { label: string; busy: boolean; onPress: () => void };
}) {
    return (
        <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-4 bg-background px-6 text-center">
            <ShieldAlert className="h-10 w-10 text-muted-foreground" />
            <div>
                <h1 className="text-lg font-semibold">{title}</h1>
                <p className="mx-auto mt-1.5 max-w-sm text-sm text-muted-foreground">{body}</p>
            </div>
            {action && (
                <Button variant="outline" disabled={action.busy} onClick={action.onPress}>
                    {action.busy && <Loader2 className="h-4 w-4 animate-spin" />}
                    {action.label}
                </Button>
            )}
        </div>
    );
}
